import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Progress } from '../ui/progress';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { 
  TrendingUp, 
  TrendingDown, 
  Minus, 
  Download, 
  Filter,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Shield,
  Activity
} from 'lucide-react';
import { PolicyTrendMetrics, AnalyticsTimeRange, ExportOptions } from '../../types/AnalyticsTypes';

interface PolicyTrendsReportProps {
  data: PolicyTrendMetrics;
  dateRange: AnalyticsTimeRange;
  onExport: (options: ExportOptions) => void;
}

const COLORS = ['#3B82F6', '#10B981', '#FBBF24', '#EF4444', '#8B5CF6', '#F59E0B'];

export default function PolicyTrendsReport({ data, dateRange, onExport }: PolicyTrendsReportProps) {
  const [selectedView, setSelectedView] = useState<'trends' | 'performance' | 'failures' | 'compliance'>('trends');

  const getTrendIcon = (trend: 'improving' | 'declining' | 'stable') => {
    switch (trend) {
      case 'improving':
        return <TrendingUp className="h-4 w-4 text-verified-green" />;
      case 'declining':
        return <TrendingDown className="h-4 w-4 text-red-500" />;
      default:
        return <Minus className="h-4 w-4 text-warm-slate" />;
    }
  };

  const getTrendColor = (trend: 'improving' | 'declining' | 'stable') => {
    switch (trend) {
      case 'improving':
        return 'text-verified-green';
      case 'declining':
        return 'text-red-500';
      default:
        return 'text-warm-slate';
    }
  };

  const getFailureTrendIcon = (trend: 'increasing' | 'decreasing' | 'stable') => {
    switch (trend) {
      case 'increasing':
        return <TrendingUp className="h-4 w-4 text-red-500" />;
      case 'decreasing':
        return <TrendingDown className="h-4 w-4 text-verified-green" />;
      default:
        return <Minus className="h-4 w-4 text-warm-slate" />;
    }
  };

  const getRiskLevelColor = (riskLevel: 'low' | 'medium' | 'high') => {
    switch (riskLevel) {
      case 'low':
        return 'bg-verified-green text-white';
      case 'medium':
        return 'bg-amber-500 text-white';
      case 'high':
        return 'bg-red-500 text-white';
    }
  };

  const handleExport = () => {
    onExport({
      format: 'pdf',
      includeCharts: true,
      includeRawData: true,
      dateRange,
      reportTypes: ['policies']
    });
  };

  // Prepare chart data
  const failureReasonsData = data.failureReasons.map((item, index) => ({
    name: item.reason,
    value: item.count,
    percentage: item.percentage,
    color: COLORS[index % COLORS.length]
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-display text-charcoal-ink">Policy Trends Report</h2>
          <p className="text-warm-slate">
            Monitor policy evaluation trends, pass/fail rates, and compliance patterns
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4 mr-2" />
            Filter
          </Button>
          <Button onClick={handleExport} size="sm" className="credential-capital-button-primary">
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="credential-capital-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-warm-slate">Total Evaluations</p>
                <p className="text-2xl font-semibold text-charcoal-ink">
                  {data.totalPolicyEvaluations.toLocaleString()}
                </p>
              </div>
              <div className="p-3 rounded-full bg-electric-steel/10">
                <Activity className="h-6 w-6 text-electric-steel" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="credential-capital-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-warm-slate">Pass Rate</p>
                <p className="text-2xl font-semibold text-verified-green">
                  {data.passRate.toFixed(1)}%
                </p>
              </div>
              <div className="p-3 rounded-full bg-verified-green/10">
                <CheckCircle className="h-6 w-6 text-verified-green" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="credential-capital-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-warm-slate">Fail Rate</p>
                <p className="text-2xl font-semibold text-red-500">
                  {data.failRate.toFixed(1)}%
                </p>
              </div>
              <div className="p-3 rounded-full bg-red-500/10">
                <XCircle className="h-6 w-6 text-red-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="credential-capital-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-warm-slate">Avg Evaluation Time</p>
                <p className="text-2xl font-semibold text-charcoal-ink">
                  {(data.policyPerformance.reduce((sum, p) => sum + p.avgEvaluationTime, 0) / data.policyPerformance.length).toFixed(1)}s
                </p>
              </div>
              <div className="p-3 rounded-full bg-electric-steel/10">
                <Clock className="h-6 w-6 text-electric-steel" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* View Selector */}
      <Card className="credential-capital-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-display text-charcoal-ink">Policy Analysis</CardTitle>
              <CardDescription>
                Detailed insights into policy evaluation patterns and performance
              </CardDescription>
            </div>
            <div className="flex space-x-2">
              {[
                { key: 'trends', label: 'Trends Over Time' },
                { key: 'performance', label: 'Policy Performance' },
                { key: 'failures', label: 'Failure Analysis' },
                { key: 'compliance', label: 'Compliance by Industry' }
              ].map(view => (
                <Button
                  key={view.key}
                  variant={selectedView === view.key ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedView(view.key as any)}
                >
                  {view.label}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {selectedView === 'trends' && (
            <div className="space-y-6">
              <div>
                <h4 className="font-medium text-charcoal-ink mb-4">Policy Evaluation Trends</h4>
                <ResponsiveContainer width="100%" height={400}>
                  <AreaChart data={data.trendsOverTime}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(value) => new Date(value).toLocaleDateString()}
                    />
                    <YAxis />
                    <Tooltip 
                      labelFormatter={(value) => new Date(value).toLocaleDateString()}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="passCount" 
                      stackId="1" 
                      stroke="#10B981" 
                      fill="#10B981" 
                      fillOpacity={0.8}
                      name="Passed"
                    />
                    <Area 
                      type="monotone" 
                      dataKey="failCount" 
                      stackId="1" 
                      stroke="#EF4444" 
                      fill="#EF4444" 
                      fillOpacity={0.8}
                      name="Failed"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div>
                <h4 className="font-medium text-charcoal-ink mb-4">Pass Rate Trend</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={data.trendsOverTime}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(value) => new Date(value).toLocaleDateString()}
                    />
                    <YAxis domain={[0, 100]} />
                    <Tooltip 
                      labelFormatter={(value) => new Date(value).toLocaleDateString()}
                      formatter={(value: any) => [`${value.toFixed(1)}%`, 'Pass Rate']}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="passRate" 
                      stroke="#3B82F6" 
                      strokeWidth={3}
                      dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {selectedView === 'performance' && (
            <div>
              <h4 className="font-medium text-charcoal-ink mb-4">Individual Policy Performance</h4>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Policy Name</TableHead>
                    <TableHead>Evaluations</TableHead>
                    <TableHead>Pass Rate</TableHead>
                    <TableHead>Avg Time</TableHead>
                    <TableHead>Trend</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.policyPerformance.map((policy) => (
                    <TableRow key={policy.policyId}>
                      <TableCell className="font-medium">{policy.policyName}</TableCell>
                      <TableCell>{policy.evaluations.toLocaleString()}</TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Progress value={policy.passRate} className="flex-1" />
                          <span className="text-sm font-medium">{policy.passRate.toFixed(1)}%</span>
                        </div>
                      </TableCell>
                      <TableCell>{policy.avgEvaluationTime.toFixed(1)}s</TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          {getTrendIcon(policy.trend)}
                          <span className={`text-sm ${getTrendColor(policy.trend)}`}>
                            {policy.trend}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={policy.passRate >= 90 ? 'default' : policy.passRate >= 80 ? 'secondary' : 'destructive'}
                        >
                          {policy.passRate >= 90 ? 'Excellent' : policy.passRate >= 80 ? 'Good' : 'Needs Attention'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {selectedView === 'failures' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-charcoal-ink mb-4">Failure Reasons Distribution</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={failureReasonsData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percentage }) => `${name} ${percentage.toFixed(1)}%`}
                    >
                      {failureReasonsData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div>
                <h4 className="font-medium text-charcoal-ink mb-4">Failure Reason Details</h4>
                <div className="space-y-4">
                  {data.failureReasons.map((reason, index) => (
                    <Card key={reason.reason} className="border">
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-medium text-charcoal-ink">{reason.reason}</h5>
                          <div className="flex items-center space-x-2">
                            {getFailureTrendIcon(reason.trend)}
                            <Badge variant="outline" className="text-xs">
                              {reason.trend}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-warm-slate">
                            {reason.count.toLocaleString()} failures
                          </span>
                          <span className="font-medium">
                            {reason.percentage.toFixed(1)}% of total
                          </span>
                        </div>
                        <Progress value={reason.percentage} className="mt-2" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          )}

          {selectedView === 'compliance' && (
            <div className="space-y-6">
              <div>
                <h4 className="font-medium text-charcoal-ink mb-4">Compliance Rates by Industry</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.complianceByIndustry}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="industry" />
                    <YAxis domain={[0, 100]} />
                    <Tooltip formatter={(value: any) => [`${value.toFixed(1)}%`, 'Pass Rate']} />
                    <Bar dataKey="passRate" fill="#3B82F6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div>
                <h4 className="font-medium text-charcoal-ink mb-4">Industry Compliance Details</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Industry</TableHead>
                      <TableHead>Pass Rate</TableHead>
                      <TableHead>Total Evaluations</TableHead>
                      <TableHead>Risk Level</TableHead>
                      <TableHead>Compliance Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.complianceByIndustry.map((industry) => (
                      <TableRow key={industry.industry}>
                        <TableCell className="font-medium">{industry.industry}</TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <Progress value={industry.passRate} className="flex-1" />
                            <span className="text-sm font-medium">{industry.passRate.toFixed(1)}%</span>
                          </div>
                        </TableCell>
                        <TableCell>{industry.totalEvaluations.toLocaleString()}</TableCell>
                        <TableCell>
                          <Badge className={getRiskLevelColor(industry.riskLevel)}>
                            {industry.riskLevel.toUpperCase()}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            {industry.passRate >= 95 ? (
                              <CheckCircle className="h-4 w-4 text-verified-green" />
                            ) : industry.passRate >= 85 ? (
                              <AlertTriangle className="h-4 w-4 text-amber-500" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-500" />
                            )}
                            <span className="text-sm">
                              {industry.passRate >= 95 ? 'Excellent' : 
                               industry.passRate >= 85 ? 'Good' : 'Needs Improvement'}
                            </span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}